import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import LineArrowsRightDownIcon from 'components/Luxkit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray, groupBy, min } from 'lib/array/arrayUtils'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import MobileMultipleRoomsCarouselPackageOption from './MobileMultipleRoomsCarouselPackageOption'
import { rem } from 'polished'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TextLink from 'components/Luxkit/TextLink'
import MobileMultipleRoomsRoomOptionPriceDetails from './MobileMultipleRoomsRoomOptionPriceDetails'
import Pane from 'components/Common/Pane'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { MultipleRoomPackage, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useLoyaltyOfferPageTargetRoomUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetRoomUpgrade'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import { isHotelPackage } from 'lib/offer/offerTypes'
import LineArrowDownIcon from 'components/Luxkit/Icons/line/LineArrowDownIcon'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ModalContext from 'contexts/ModalContext'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import Sheet from 'components/utils/Sheet'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import OfferPageBNBLcta from 'components/OfferPage/OfferPageBNBLcta'
import config from 'constants/config'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'

const Container = styled.div`
  padding: ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  packageOptions: Array<OfferPackageOptionView>;
  rooms: Array<App.RoomOccupants>;
  checkIn?: string;
  isFlightsSelected?: boolean;
  userMultiItemCartEnabled: boolean;
  multipleRoomPackages: Array<MultipleRoomPackage>;
  showSelection?: boolean;
  canRedeemLuxPlusBenefits: boolean;
  headerRef: React.RefObject<HTMLDivElement | null>;
  duration: number;
}

function MobileMultipleRoomsSameRoomOption(props: Props) {
  const { offer, packageOptions, userMultiItemCartEnabled, rooms, checkIn, isFlightsSelected, multipleRoomPackages, canRedeemLuxPlusBenefits, showSelection, headerRef, duration } = props

  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)
  const currentContact = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)

  // Same room type for all options, so we pick the first one
  const firstPackageOption = packageOptions?.[0]?.package
  const pkg = isHotelPackage(firstPackageOption) ? firstPackageOption : undefined
  const roomType = packageOptions[0].package.roomType

  const minAvailableRooms = min(packageOptions, option => (option.availableRooms ?? 0))?.availableRooms
  const availableRooms = packageOptions.filter(option => (option.availableRooms ?? 0) >= rooms.length)
  const isAllRoomsAvailable = packageOptions.every(option => option.availableRooms && option.availableRooms >= rooms.length)
  const isSoldOut = packageOptions.every(option => !option.availableRooms)
  const hasExceededCapacity = packageOptions.every(option => option.hotelAvailable && !option.capacityValid)
  const hasHotelMemberPrice = packageOptions.some(option => option.hotelMemberPrice > 0)
  const isSelectionEnabled = isAllRoomsAvailable && !isSoldOut && !hasExceededCapacity

  const carouselRef = useRef<HTMLDivElement | null>(null)

  const packageViewsByUniqueKey = useMemo(() => {
    return groupBy(packageOptions, key => key.package.uniqueKey)
  }, [packageOptions])

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)
  const shouldPreventMultipleRoomsBooking = rooms.length > 2 && canRedeemLuxPlusBenefits && hasHotelMemberPrice

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetRoomUpgrade(pkg)
  const canAddToUserCart = !!checkIn && userMultiItemCartEnabled && !selectedTargetUpgradePackage
  const showBNBL = !selectedTargetUpgradePackage && offer.hasBuyNowBookLater && config.accommodation.hasBnbl

  const onOpenCapacity = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const onSelectIndividual = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMobileMultiRoomMode, mobileMultiRoomMode: 'individual' })
    headerRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
  }, [offerPageDispatch, headerRef])

  const selectedPackageOptionViewIndex = useMemo(() => {
    return availableRooms.findIndex(packageView => multipleRoomPackages.find(pkg => pkg.packageId && packageView.package.uniqueKey === getPackageUniqueKey(pkg.packageId, duration, pkg.roomRateId))) + 1
  }, [availableRooms, duration, multipleRoomPackages])

  const onSubmit = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const packageKey = e.currentTarget.dataset.packageUniqueKey
    if (!packageKey) return

    const packagesByKey = packageViewsByUniqueKey.get(packageKey)
    if (!packagesByKey?.length) return

    const roomsPackages = rooms.map(room => ({
      room,
      packageOption: packagesByKey[0].package,
    }))
    offerPageDispatch({
      type: OfferPageActions.selectRoomsPackages,
      roomsPackages,
    })
  }, [offerPageDispatch, packageViewsByUniqueKey, rooms])

  useEffect(() => {
    if (!carouselRef.current) return
    if (selectedPackageOptionViewIndex && showSelection) {
      const selectedElement = carouselRef.current.children[selectedPackageOptionViewIndex - 1]
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' })
      }
    }
  }, [selectedPackageOptionViewIndex, showSelection])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS"/>)
  }, [showModal])

  if (!pkg || !roomType) {
    return null
  }

  return (
    <Pane key={roomType.id}>
      {!!selectedPackageOptionViewIndex && showSelection && <Sheet background="highlight-primary" padding="4 20">
        <CaptionTextBlock variant="medium" startIcon={<LineArrowDownIcon />}>Your current selection</CaptionTextBlock>
      </Sheet>}
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage roomPackage={pkg} />
      </AccommodationRoomTile>
      <Container>
        <Group direction="vertical" gap={16}>
          <Group direction="vertical" gap={12}>
            <Group direction="horizontal" gap={8}>
              <StructureLabel variant="standard">{rooms.length}x</StructureLabel>
              <Heading variant="heading4">{roomType.name}</Heading>
            </Group>
            <LabelGroup>
              {!!minAvailableRooms && <RoomsRemainingLabel roomCount={minAvailableRooms} threshold="rate" />}
              {!!roomType.stats.booked && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
            </LabelGroup>
            <LabelGroup>
              {!!roomType.sizeSqm && <StructureLabel variant="light" startIcon={<LineArrowsRightDownIcon />}>{roomType.sizeSqm} m&sup2;</StructureLabel>}
              {!!roomType.numberOfBedrooms && <StructureLabel variant="light" startIcon={<LineBedIcon />}>{roomType.numberOfBedrooms} bedrooms</StructureLabel>}
            </LabelGroup>
          </Group>
          {isSelectionEnabled && !shouldPreventMultipleRoomsBooking && (
            <CardCarousel snap="start" width={availableRooms.length === 1 ? '100%' : 'auto'} gap={12} ref={carouselRef}>
              {availableRooms.map((packageView, index) => <MobileMultipleRoomsCarouselPackageOption
                key={packageView.package.uniqueKey}
                packageView={packageView}
                childrenCtaDirection="vertical"
                packagePosition={availableRooms.length > 1 ? index + 1 : undefined}
                isSelected={showSelection && multipleRoomPackages.some(pkg => pkg.packageId && packageView.package.uniqueKey === getPackageUniqueKey(pkg.packageId, duration, pkg.roomRateId))}
              >
                <Group direction="vertical" gap={12} fullWidth>
                  <Group direction="horizontal" horizontalAlign="stretch" gap={8} fullWidth>
                    {canAddToUserCart && !isFlightsSelected && <OfferAddToCartButton
                      size="large"
                      fit="flex"
                      packageView={packageView}
                    />}
                    {!isFlightsSelected && <OfferBookButton
                      upgradeEnabled={false}
                      size="large"
                      fit="flex"
                      data-testid="HotelBookingBtn"
                      data-package-unique-key={packageView.package.uniqueKey}
                      onSubmit={onSubmit}
                      packageView={packageView}
                    >
                      {checkIn ? `Book ${rooms.length} rooms` : 'Check availability'}
                    </OfferBookButton>}
                    {isFlightsSelected && <OfferBookButton
                      upgradeEnabled={false}
                      size="large"
                      fit="flex"
                      data-testid="HotelAndFlightsBtn"
                      data-buytype="flights"
                      packageView={packageView}
                    >
                      Select {rooms.length} rooms
                    </OfferBookButton>}
                  </Group>
                  {showBNBL && packageView.capacityValid && packageView.package.allowBuyNowBookLater && <OfferPageBNBLcta packageView={packageView} direction="horizontal" />}
                </Group>
              </MobileMultipleRoomsCarouselPackageOption>)}
            </CardCarousel>
          )}
          {shouldPreventMultipleRoomsBooking && (
            <Sheet background="neutral-seven" padding={20}>
              <Group direction="vertical" gap={8}>
                <Group direction="vertical" gap={4}>
                  <BodyText variant="medium" weight="bold">Ready to book? Call us to complete your booking</BodyText>
                  <BodyText variant="medium">
                    Our team are available {NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? '7 days a week from 9am to 7pm' : '24 hours, 7 days'}
                  </BodyText>
                </Group>
                <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${currentContact.defaultContact.number}`} fit="flex">
                  Call {currentContact.defaultContact.humanReadable}
                </TextButton>
                {callbacksFeatureEnabled && (
                  <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
                    Request a callback
                  </TextButton>
                )}
              </Group>
            </Sheet>
          )}
          {!isSelectionEnabled && !shouldPreventMultipleRoomsBooking && (
            <Group direction="vertical" gap={12}>
              <MobileMultipleRoomsRoomOptionPriceDetails
                room={rooms[0]}
                packageOption={packageOptions[0]}
              />
              {isSoldOut && (
                <MessageBanner
                  density="compact"
                  kind="informational-primary"
                  description="This room is sold out"
                />
              )}
              {!isSoldOut && hasExceededCapacity && (
                <MessageBanner
                  density="compact"
                  kind="informational-primary"
                  description={<>
                    Room capacity reached. To select it please{' '}
                    <TextLink onClick={onOpenCapacity}>revise number of guests</TextLink>.
                  </>}
                />
              )}
              {!isSoldOut && !hasExceededCapacity && !isAllRoomsAvailable && (
                <MessageBanner
                  density="compact"
                  kind="informational-primary"
                  description={<>
                    This room type works for some rooms in your group, but not all. To select it, try{' '}
                    <TextLink onClick={onSelectIndividual}>choosing rooms individually</TextLink>.
                  </>}
                />
              )}
            </Group>
          )}
        </Group>
      </Container>
    </Pane>
  )
}

type MappedProps = Pick<Props,
  'offer'
  | 'rooms'
  | 'checkIn'
  | 'isFlightsSelected'
  | 'userMultiItemCartEnabled'
  | 'multipleRoomPackages'
  | 'canRedeemLuxPlusBenefits'
  | 'duration'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  duration: state.duration,
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  multipleRoomPackages: state.multipleRoomPackages,
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
}), MobileMultipleRoomsSameRoomOption)
